/** Pooled battlefield VFX: trails, smoke, sparks, flashes, rings, debris. */

export type VfxKind =
  | 'spark'
  | 'smoke'
  | 'flash'
  | 'ember'
  | 'debris'
  | 'shock'
  | 'plasma'
  | 'dust'
  | 'vapour'
  | 'flame';

export interface VfxParticle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  r: number;
  r0: number;
  c: number;
  kind: VfxKind;
  drag: number;
  gravity: number;
  rot: number;
  rotV: number;
  alpha0: number;
  alive: boolean;
}

export interface TrailPoint {
  x: number;
  y: number;
  t: number;
}

export interface ShockRing {
  x: number;
  y: number;
  r: number;
  maxR: number;
  life: number;
  maxLife: number;
  c: number;
  w: number;
  alive: boolean;
}

export interface FlashSprite {
  x: number;
  y: number;
  life: number;
  maxLife: number;
  r: number;
  c: number;
  kind: 'muzzle' | 'impact' | 'blast' | 'charge' | 'slash';
  angle: number;
  alive: boolean;
}

export interface ArcBolt {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  c: number;
  life: number;
  maxLife: number;
  w: number;
  jagged: boolean;
}

const MAX_PARTICLES = 900;
const MAX_RINGS = 80;
const MAX_FLASHES = 80;

export class VfxSystem {
  private pool: VfxParticle[] = [];
  private active: VfxParticle[] = [];
  rings: ShockRing[] = [];
  flashes: FlashSprite[] = [];
  arcs: ArcBolt[] = [];
  /** Particle density multiplier driven by the fidelity setting (0.45 / 0.7 / 1). */
  density = 1;

  constructor() {
    for (let i = 0; i < MAX_PARTICLES; i++) {
      this.pool.push(this.makeDead());
    }
  }

  private scaled(n: number): number {
    return Math.max(0, Math.round(n * this.density));
  }

  private makeDead(): VfxParticle {
    return {
      x: 0, y: 0, vx: 0, vy: 0, life: 0, maxLife: 1,
      r: 1, r0: 1, c: 0xffffff, kind: 'spark',
      drag: 0.95, gravity: 0, rot: 0, rotV: 0, alpha0: 1, alive: false,
    };
  }

  private take(): VfxParticle | null {
    const p = this.pool.pop();
    if (p) return p;
    if (this.active.length > MAX_PARTICLES) return null;
    return this.makeDead();
  }

  spawn(partial: Partial<VfxParticle> & { x: number; y: number; kind: VfxKind; c: number }): void {
    const p = this.take();
    if (!p) return;
    const life = partial.maxLife ?? partial.life ?? 0.4;
    p.x = partial.x;
    p.y = partial.y;
    p.vx = partial.vx ?? 0;
    p.vy = partial.vy ?? 0;
    p.life = life;
    p.maxLife = life;
    p.r = partial.r ?? 2;
    p.r0 = p.r;
    p.c = partial.c;
    p.kind = partial.kind;
    p.drag = partial.drag ?? 0.92;
    p.gravity = partial.gravity ?? 0;
    p.rot = partial.rot ?? 0;
    p.rotV = partial.rotV ?? 0;
    p.alpha0 = partial.alpha0 ?? 1;
    p.alive = true;
    this.active.push(p);
  }

  burst(x: number, y: number, c: number, n: number, opts: {
    speed?: number;
    kind?: VfxKind;
    life?: number;
    r?: number;
    gravity?: number;
  } = {}): void {
    const sp = opts.speed ?? 90;
    const kind = opts.kind ?? 'spark';
    const life = opts.life ?? 0.35;
    const r = opts.r ?? 2;
    for (let i = 0; i < this.scaled(n); i++) {
      const a = Math.random() * Math.PI * 2;
      const s = sp * (0.35 + Math.random() * 0.85);
      this.spawn({
        x, y,
        vx: Math.cos(a) * s,
        vy: Math.sin(a) * s,
        kind, c,
        maxLife: life * (0.6 + Math.random() * 0.8),
        r: r * (0.6 + Math.random() * 0.8),
        drag: 0.9,
        gravity: opts.gravity ?? 0,
      });
    }
  }

  ring(x: number, y: number, maxR: number, c: number, w = 3, life = 0.45): void {
    if (this.rings.length >= MAX_RINGS) this.rings.shift();
    this.rings.push({
      x, y, r: maxR * 0.08, maxR, life, maxLife: life, c, w, alive: true,
    });
  }

  flash(x: number, y: number, r: number, c: number, kind: FlashSprite['kind'], angle = 0, life = 0.12): void {
    if (this.flashes.length >= MAX_FLASHES) this.flashes.shift();
    this.flashes.push({
      x, y, life, maxLife: life, r, c, kind, angle, alive: true,
    });
  }

  /** Short-lived lightning segment (Tesla arcs / chain lightning). */
  arc(x1: number, y1: number, x2: number, y2: number, c: number, life = 0.15, w = 2): void {
    if (this.arcs.length > 120) this.arcs.shift();
    this.arcs.push({ x1, y1, x2, y2, c, life, maxLife: life, w, jagged: true });
  }

  /** Straight focused beam (laser emitter). */
  beam(x1: number, y1: number, x2: number, y2: number, c: number, life = 0.12, w = 3): void {
    if (this.arcs.length > 120) this.arcs.shift();
    this.arcs.push({ x1, y1, x2, y2, c, life, maxLife: life, w, jagged: false });
  }

  /** Vapour contrail puff behind a fast projectile. */
  vapour(x: number, y: number, c: number, scale = 1): void {
    this.spawn({
      x: x + (Math.random() - 0.5) * 3,
      y: y + (Math.random() - 0.5) * 3,
      vx: (Math.random() - 0.5) * 12,
      vy: (Math.random() - 0.5) * 12 - 8,
      kind: 'vapour',
      c,
      maxLife: 0.45 + Math.random() * 0.35,
      r: (2.5 + Math.random() * 2.5) * scale,
      drag: 0.97,
      alpha0: 0.55,
    });
  }

  smoke(x: number, y: number, c = 0x3a3a3a, n = 1): void {
    const count = this.scaled(n);
    for (let i = 0; i < count; i++) {
      this.spawn({
        x: x + (Math.random() - 0.5) * 8,
        y: y + (Math.random() - 0.5) * 8,
        vx: (Math.random() - 0.5) * 20,
        vy: -15 - Math.random() * 25,
        kind: 'smoke',
        c,
        maxLife: 0.8 + Math.random() * 0.7,
        r: 4 + Math.random() * 6,
        drag: 0.98,
        alpha0: 0.35,
      });
    }
  }

  /** Ground dust kicked up by small-arms fire and impacts. */
  dust(x: number, y: number, c = 0x8a7a5a, n = 3): void {
    const count = this.scaled(n);
    for (let i = 0; i < count; i++) {
      const a = -Math.PI / 2 + (Math.random() - 0.5) * 2.4;
      const s = 24 + Math.random() * 46;
      this.spawn({
        x: x + (Math.random() - 0.5) * 8,
        y: y + (Math.random() - 0.5) * 4,
        vx: Math.cos(a) * s * 0.7 + (Math.random() - 0.5) * 22,
        vy: Math.sin(a) * s * 0.5 - 6,
        kind: 'dust',
        c,
        maxLife: 0.35 + Math.random() * 0.45,
        r: 3 + Math.random() * 4,
        drag: 0.9,
        gravity: 26,
        alpha0: 0.5,
      });
    }
  }

  /** Brass casing ejected from an autocannon / gatling. */
  casing(x: number, y: number, angle: number, c = 0xd9b25a): void {
    if (Math.random() > 0.65) return;
    const perp = angle + Math.PI / 2 + (Math.random() - 0.5) * 0.7;
    const s = 45 + Math.random() * 45;
    this.spawn({
      x,
      y,
      vx: Math.cos(perp) * s,
      vy: Math.sin(perp) * s - 34,
      kind: 'debris',
      c,
      maxLife: 0.5 + Math.random() * 0.35,
      r: 1.7,
      drag: 0.94,
      gravity: 230,
      rot: Math.random() * Math.PI,
      rotV: (Math.random() - 0.5) * 20,
    });
  }

  explosion(x: number, y: number, scale = 1, warm = true, smokeScale = 1): void {
    const c1 = warm ? 0xffaa33 : 0x66aaff;
    const c2 = warm ? 0xff4411 : 0x3366ff;
    const c3 = warm ? 0xffee88 : 0xaaddff;
    this.flash(x, y, 28 * scale, c3, 'blast', 0, 0.18);
    this.ring(x, y, 40 * scale, c1, 4, 0.35);
    this.ring(x, y, 28 * scale, 0xffffff, 2, 0.2);
    this.burst(x, y, c1, Math.floor(14 * scale), { speed: 160 * scale, life: 0.4, r: 3 * scale });
    this.burst(x, y, c2, Math.floor(10 * scale), { speed: 100 * scale, life: 0.55, r: 4 * scale, gravity: 40 });
    this.burst(x, y, 0xffffff, Math.floor(4 * scale), { speed: 200 * scale, life: 0.15, r: 1.5 });
    // Layered fire tongues give impacts a readable silhouette; the rings remain
    // reserved for the shockwave rather than carrying the whole explosion.
    for (let i = 0; i < this.scaled(Math.max(4, 8 * scale)); i++) {
      const a = Math.random() * Math.PI * 2;
      const s = (42 + Math.random() * 90) * scale;
      this.spawn({
        x: x + (Math.random() - 0.5) * 6 * scale,
        y: y + (Math.random() - 0.5) * 6 * scale,
        vx: Math.cos(a) * s,
        vy: Math.sin(a) * s - 28 * scale,
        kind: 'flame', c: i % 2 ? c1 : c3,
        maxLife: (0.22 + Math.random() * 0.28) * Math.min(1.5, scale),
        r: (3 + Math.random() * 4) * scale,
        gravity: 65,
        drag: 0.9,
        alpha0: 0.9,
      });
    }
    if (smokeScale > 0) this.smoke(x, y, 0x2a2420, Math.floor(3 * scale * smokeScale));
  }

  artilleryImpact(x: number, y: number, radius: number, smokeScale = 1, warm = true): void {
    this.explosion(x, y, 1.4, warm, smokeScale);
    const ringColour = warm ? 0xd4a24a : 0xb878ff;
    const innerColour = warm ? 0xff8844 : 0xe2c4ff;
    const emberColour = warm ? 0xff6622 : 0xb878ff;
    this.ring(x, y, radius, ringColour, 5, 0.5);
    this.ring(x, y, radius * 0.7, innerColour, 3, 0.35);
    for (let i = 0; i < 8; i++) {
      const a = Math.random() * Math.PI * 2;
      const d = Math.random() * radius * 0.6;
      this.spawn({
        x: x + Math.cos(a) * d,
        y: y + Math.sin(a) * d,
        vx: Math.cos(a) * 30,
        vy: Math.sin(a) * 30 - 20,
        kind: 'ember',
        c: emberColour,
        maxLife: 0.5 + Math.random() * 0.4,
        r: 2,
        gravity: 80,
      });
    }
  }

  update(dt: number): void {
    for (let i = this.active.length - 1; i >= 0; i--) {
      const p = this.active[i];
      p.life -= dt;
      if (p.life <= 0) {
        p.alive = false;
        this.active.splice(i, 1);
        if (this.pool.length < MAX_PARTICLES) this.pool.push(p);
        continue;
      }
      p.vx *= Math.pow(p.drag, dt * 60);
      p.vy *= Math.pow(p.drag, dt * 60);
      p.vy += p.gravity * dt;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.rot += p.rotV * dt;
      if (p.kind === 'smoke' || p.kind === 'vapour') {
        p.r = p.r0 * (1 + (1 - p.life / p.maxLife) * 1.8);
      } else {
        p.r = p.r0 * (p.life / p.maxLife);
      }
    }
    for (let i = this.rings.length - 1; i >= 0; i--) {
      const r = this.rings[i];
      r.life -= dt;
      if (r.life <= 0) {
        r.alive = false;
        this.rings.splice(i, 1);
        continue;
      }
      const t = 1 - r.life / r.maxLife;
      // ease-out expansion
      r.r = r.maxR * (1 - Math.pow(1 - t, 3));
    }
    for (let i = this.flashes.length - 1; i >= 0; i--) {
      const f = this.flashes[i];
      f.life -= dt;
      if (f.life <= 0) {
        f.alive = false;
        this.flashes.splice(i, 1);
      }
    }
    for (let i = this.arcs.length - 1; i >= 0; i--) {
      this.arcs[i].life -= dt;
      if (this.arcs[i].life <= 0) this.arcs.splice(i, 1);
    }
  }

  get particles(): VfxParticle[] {
    return this.active;
  }

  clear(): void {
    for (const p of this.active) {
      p.alive = false;
      if (this.pool.length < MAX_PARTICLES) this.pool.push(p);
    }
    this.active.length = 0;
    this.rings.length = 0;
    this.flashes.length = 0;
    this.arcs.length = 0;
  }
}

/** Rolling trail history for projectiles (vapour / smoke contrails). */
export class TrailHistory {
  points: TrailPoint[] = [];
  private max = 18;
  private acc = 0;
  private interval = 0.018;

  reset(): void {
    this.points.length = 0;
    this.acc = 0;
  }

  push(x: number, y: number, dt: number): void {
    this.acc += dt;
    if (this.acc < this.interval) return;
    this.acc = 0;
    this.points.push({ x, y, t: 0.35 });
    if (this.points.length > this.max) this.points.shift();
  }

  tick(dt: number): void {
    for (let i = this.points.length - 1; i >= 0; i--) {
      this.points[i].t -= dt;
      if (this.points[i].t <= 0) this.points.splice(i, 1);
    }
  }
}
